import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDebounce } from 'hooks/useDebounce';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSignUpUserAndOrg, useSignUpOrgNameValidate } from './hooks';
import { useSignUpUserAndOrgValidate } from './hooks/useSignUpUserAndOrgValidate';
import { SignUpType } from '../../../auth';

export interface SignUpField {
    value: string;
    errorMsg: string | null;
}

export interface ISignUpManager {
    signUpEnabled: boolean;
    SSOSignUpEnabled: boolean;
    email: SignUpField;
    password: SignUpField;
    jobTitle: SignUpField;
    organizationName: SignUpField;
    name: SignUpField;
    hasAcceptedTerms: boolean;
    validCaptcha: boolean;
    isOrgNameValid: boolean;
    orgValidationSuccess: boolean;
    setHasAcceptedTerms: React.Dispatch<React.SetStateAction<boolean>>;
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleJobTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleOrganizationNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSignUp: () => void;
    handleLinkedInSignUp: () => void;
}

const useSignUpManager = (): ISignUpManager => {
    const [email, setEmail] = useState<SignUpField>({ value: '', errorMsg: null });
    const [password, setPassword] = useState<SignUpField>({ value: '', errorMsg: null });
    const [jobTitle, setJobTitle] = useState<SignUpField>({ value: '', errorMsg: null });
    const [organizationName, setOrganizationName] = useState<SignUpField>({ value: '', errorMsg: null });
    const [name, setName] = useState<SignUpField>({ value: '', errorMsg: null });
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(true); // Default to true. Currently creating account from deal accepts terms by default.
    const [validCaptcha, setValidCaptcha] = useState(true);
    const [blockSignUp, setBlockSignUp] = useState(false); // Block sign-up for actions like typing org name

    // Debounced password value
    const debouncedPassword = useDebounce(password.value, 300);
    const debouncedOrganizationName = useDebounce(organizationName.value, 500);

    const { data: { isValid: isOrgNameValid = true } = {}, isLoading: validatingOrgName } = useSignUpOrgNameValidate(
        debouncedOrganizationName,
        {
            enabled: !!debouncedOrganizationName,
        },
    );

    const orgValidationSuccess = !validatingOrgName && isOrgNameValid && !!debouncedOrganizationName && !blockSignUp;

    const { mutate: signUpUserAndOrg } = useSignUpUserAndOrg();

    const { mutateAsync: validateSignUpUserAndOrg } = useSignUpUserAndOrgValidate();

    const { executeRecaptcha } = useGoogleReCaptcha();

    // matches password requirements from Auth0
    const validatePasswordStrength = (password: string): string | null => {
        const errors: string[] = [];

        if (password.length < 6) {
            errors.push('Password must be at least 6 characters long.');
        }

        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter (a-z).');
        }

        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter (A-Z).');
        }

        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number (0-9).');
        }

        return errors.length ? errors.join(' ') : null;
    };

    useEffect(() => {
        if (debouncedPassword) {
            const result = validatePasswordStrength(debouncedPassword);
            setPassword(prev => ({ ...prev, errorMsg: result }));
        } else {
            setPassword(prev => ({ ...prev, errorMsg: null }));
        }
    }, [debouncedPassword]);

    useEffect(() => {
        setBlockSignUp(false);
    }, [debouncedOrganizationName]);

    const signUpEnabled: boolean = Boolean(
        email.value
        && password.value
        && jobTitle.value
        && organizationName.value
        && name.value
        && hasAcceptedTerms
        && validCaptcha
        && isOrgNameValid
        && !email.errorMsg
        && !password.errorMsg
        && !jobTitle.errorMsg
        && !organizationName.errorMsg
        && !name.errorMsg,
    );

    const SSOSignUpEnabled = orgValidationSuccess;

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setName({ value, errorMsg: null });
    };

    const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setJobTitle({ value, errorMsg: null });
    };

    const handleOrganizationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setOrganizationName({ value, errorMsg: null });
        setBlockSignUp(true);
    };

    const { loginWithRedirect } = useAuth0();

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        setEmail({ value, errorMsg: isValid ? null : 'Invalid email format' });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentPassword = e.target.value;
        setPassword(prev => ({ ...prev, value: currentPassword }));
    };

    const handleSignupValidation = async (): Promise<boolean> => {
        try {
            if (!executeRecaptcha) {
                console.error('Execute recaptcha not yet available');
                return false;
            }

            const token = await executeRecaptcha('signUp');
            if (!token) {
                console.error('Recaptcha token not received');
                return false;
            }

            const result = await validateSignUpUserAndOrg({
                organizationName: organizationName.value,
                email: email.value,
                captchaToken: token,
            });

            if (!result) {
                return false;
            }

            const isValid = result.validEmail && result.validOrgName && result.validCaptcha;

            if (!result.validEmail) {
                setEmail(prev => ({ ...prev, errorMsg: 'Email already exists' }));
            }
            if (!result.validOrgName || result.matchingOrganizations) {
                setOrganizationName(prev => ({ ...prev, errorMsg: 'This organization already exists' }));
            }
            if (!result.validCaptcha) {
                setValidCaptcha(false);
            }

            return isValid;
        } catch (error) {
            console.error('Validation error:', error);
            return false;
        }
    };

    const handleSignUp = async () => {
        if (!signUpEnabled) return;

        const isValid = await handleSignupValidation();
        if (!isValid) return;

        signUpUserAndOrg({
            organizationName: organizationName.value,
            name: name.value,
            email: email.value,
            workEmail: email.value,
            password: password.value,
            termsAccepted: hasAcceptedTerms,
        }, {
            onSuccess: () => {
                loginWithRedirect({
                    authorizationParams: {
                        login_hint: email.value,
                    },
                });
            },
        });
    };

    const handleLinkedInSignUp = () => {
        if (SSOSignUpEnabled) {
            loginWithRedirect({
                authorizationParams: {
                    connection: 'linkedin',
                    prompt: 'login',
                },
                appState: {
                    organizationName: organizationName.value,
                    signupType: SignUpType.SOCIAL,
                },
            });
        }
    };

    return {
        signUpEnabled,
        SSOSignUpEnabled,
        email,
        password,
        jobTitle,
        organizationName,
        name,
        hasAcceptedTerms,
        validCaptcha,
        isOrgNameValid,
        orgValidationSuccess,
        setHasAcceptedTerms,
        handleEmailChange,
        handlePasswordChange,
        handleJobTitleChange,
        handleOrganizationNameChange,
        handleNameChange,
        handleSignUp,
        handleLinkedInSignUp,
    };
};

export default useSignUpManager;
